import * as React from "react";
import { Controller, useFormContext } from "react-hook-form";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { Avatar, Chip, FormHelperText } from "@mui/material";
import { AttentionSeeker } from "react-awesome-reveal";
import { autoCompleteStyles } from "./MUIstyles";
import { useTranslation } from "react-i18next";

type OfficeUser = {
  id: string;
  name: string;
  phonenum: string;
  profile_pic: string;
};

type OfficePersonAutoCompleteProps = {
  name: string;
  label: string;
  officeUserData: OfficeUser[];
  multi?: boolean;
};

const OfficePersonAutoComplete = ({
  name,
  label,
  officeUserData,
  multi = false,
}: OfficePersonAutoCompleteProps) => {
  const { control } = useFormContext();
  const classes = autoCompleteStyles();
  const validUserData = officeUserData.filter((user) => user?.name);
  const { t } = useTranslation("service_request_settings");

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={multi ? [] : null}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <Autocomplete
          multiple={multi}
          options={validUserData}
          className={classes.root}
          autoHighlight
          disableCloseOnSelect={multi}
          getOptionLabel={(option) => option?.name ?? ""}
          value={value || (multi ? [] : null)}
          onChange={(_, newValue) => onChange(newValue)}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          popupIcon={null}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <Box
                key={option.id}
                component="li"
                {...optionProps}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1.5,
                  borderRadius: "12px",
                  "&:hover": {
                    backgroundColor: "#F3F4F6",
                  },
                }}
              >
                <Avatar
                  src={option.profile_pic}
                  alt={option.name}
                  sx={{ width: 42, height: 42 }}
                />
                <Box>
                  <p className="font-semibold text-sm text-gray-800">
                    {option.name}
                  </p>
                  <p className="text-xs text-gray-500">{option.phonenum}</p>
                </Box>
              </Box>
            );
          }}
          renderTags={
            multi
              ? (selected, getTagProps) =>
                  selected.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      avatar={<Avatar src={option.profile_pic} />}
                      label={option.name}
                      sx={{ borderRadius: "8px" }}
                    />
                  ))
              : undefined
          }
          renderInput={(params) => (
            <>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">
                {label}
              </label>
              <TextField
                {...params}
                placeholder={t("choose_office_person")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "14px",
                    background: "#fff",
                    paddingY: "4px",
                  },
                }}
              />

              {error && <FormHelperText error>{error.message}</FormHelperText>}
            </>
          )}
        />
      )}
    />
  );
};

export default OfficePersonAutoComplete;
